// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { Permission } from '../constants/permissions.js';
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/**
 * The `event_type` that declares a record kind (and, per record, one window
 * for it). PLATFORM selector vocabulary in the 64.153.0 tradition; packs
 * declare it on deadline records, and nothing in `src/` branches on a pack's
 * instrument, authority or sla_type strings.
 */
export const ISSUED_RECORD_LIFECYCLE_EVENT_TYPE = 'issued_record_lifecycle';
/** The pack's retention statement for a kind — the deletion gate reads it (AC7). */
export const ISSUED_RECORD_RETENTION_EVENT_TYPE = 'issued_record_retention';
/**
 * The pack-named sharing bases for a kind: `request_types` are the basis
 * codes, `request_type_labels` their bilingual names, and the record's `note`
 * its restriction statement (AC3).
 */
export const ISSUED_RECORD_SHARING_BASIS_EVENT_TYPE = 'issued_record_sharing_basis';
/**
 * The pack's coverage vocabulary for the scope decision: `request_types` are
 * the coverage codes, `request_type_labels` their bilingual names, and the
 * record's `note` the caveat (AC2).
 */
export const ISSUED_RECORD_SCOPE_EVENT_TYPE = 'issued_record_scope';
/**
 * How a declaring record classifies the window it carries. A pack field
 * (`window_role`) persisted by the installer; a value outside this list is
 * read as `null` and shown as an unclassified window, never dropped.
 */
export const ISSUED_RECORD_WINDOW_ROLES = [
    'annual_creation',
    'annual_creation_special',
    'transition',
];
/**
 * Platform vocabulary for the evidence recorded against the record SET (AC5).
 * Four kinds because the duties are four different facts: that the set was
 * synchronised, that integration readiness was demonstrated, that a backup
 * was taken, and that a recovery was tested.
 */
export const ISSUED_RECORD_EVIDENCE_KINDS = [
    'synchronisation',
    'integration_readiness',
    'backup',
    'recovery_test',
];
/**
 * Tags on guidance records (`grc_regulatory_guidances`) the screens select to
 * render the pack's own statements. `platformBoundary` and `scopeBoundary`
 * are the 62.19.0 tags, reused so one `BoundarySection` serves both registers;
 * the other two are this register's own.
 */
export const ISSUED_RECORD_GUIDANCE_TAGS = {
    platformBoundary: 'platform_boundary',
    scopeBoundary: 'scope_boundary',
    /** No repurposing, no unlawful onward transfer — rendered on every sharing record. */
    sharingRestriction: 'sharing_restriction',
    /** Records stay accessible through and after the subject's time; retention per archival law. */
    retentionStatement: 'retention_statement',
};
/**
 * How far the erasure gate got joining a rights-request case to this register
 * (AC7). Every value is a DIFFERENT statement — the 62.14.0 `DirectionClockState`
 * lesson — and none of them is a silent `[]`:
 *
 * - `resolved` — the join ran; the targets are the records and versions the
 *   subject holds, which may legitimately be none;
 * - `no_declared_kind` — no installed pack declares a record kind for this
 *   organisation, so there is no register for the case to reach. Not a gap;
 * - `no_pack_declared_ref_key` — a kind IS declared and no declaring record
 *   names the external-ref key, so the platform cannot know whether the case
 *   reaches an issued record. The destruction is REFUSED (fail closed): a gate
 *   that cannot see is not a gate;
 * - `no_subject_profile` — the case names no data-subject profile, so it
 *   identifies no subject the register could be keyed on;
 * - `subject_profile_unreadable` — the case names a profile that does not
 *   resolve in this organisation (absent, or erased under an earlier request);
 * - `no_subject_identifier` — the profile resolves and carries NO value under
 *   any of the pack-declared keys, so there is no identifier to look the
 *   register up by (gate E12.5 pass 2, finding 6).
 *
 * The last three are RECORDED and audited on the attestation rather than
 * refusing it: the gap is in the case's own subject linkage, not in the
 * register, and a case that identifies no subject at all reaches no issued
 * record by construction. What is never allowed is the state going unsaid.
 *
 * `no_subject_identifier` exists because it was being told as `resolved` with
 * an empty identifier list (gate E12.5 pass 2, finding 6) — indistinguishable,
 * in the `state` the task records and the help article promises, from a join
 * that RAN and found the subject holds nothing. Those are different facts: the
 * first says the platform never had an identifier to search on, and the second
 * says it searched and the register held nothing. The external-ref key on a
 * data-subject profile is populated by the data-subjects module and nothing in
 * this register enforces it, so the first is the operationally likely one.
 */
export const ISSUED_RECORD_SUBJECT_LINKAGE_STATES = [
    'resolved',
    'no_declared_kind',
    'no_pack_declared_ref_key',
    'no_subject_profile',
    'subject_profile_unreadable',
    'no_subject_identifier',
];
// ── Records and versions (AC1) ──
export const ISSUED_RECORD_VERSION_KINDS = ['issued', 'reissued', 'corrected'];
/**
 * Why a sharing attempt was refused. Each is a different fact about the
 * attempt, so the refusal can say which — "no basis cited" is a different
 * remedy from "the consent exists but has been withdrawn".
 */
export const ISSUED_RECORD_SHARING_REFUSAL_REASONS = [
    'no_basis_cited',
    'basis_not_declared',
    'consent_not_found',
    'consent_not_valid',
    'minor_without_representative',
];
/**
 * How many refusals the record document keeps inline. Chosen rather than
 * unbounded because `getRecord` and `listRecords` return the whole document:
 * a record refused repeatedly used to grow without limit on the register's
 * hottest read. The count is kept separately and the audit trail keeps all.
 */
export const ISSUED_RECORD_SHARING_REFUSALS_INLINE_CAP = 50;
export const ISSUED_RECORD_SHARING_BASIS_KINDS = ['pack_basis', 'consent'];
// ── Exports (AC6) ──
/**
 * The two per-subject histories the register exports through
 * `UnifiedExportService` (R-33), each carrying the source pack code and
 * version. The retrieval grant is resolved from `(owner type, kind)` — the
 * 64.92.0 kinded-export contract — through `ISSUED_RECORD_EXPORT_GATES`.
 */
export const ISSUED_RECORD_EXPORT_KINDS = ['access_history', 'sharing_history'];
/** The attachment owner type both kinds are stored under. */
export const ISSUED_RECORD_EXPORT_OWNER_TYPE = 'issued_record_export';
/**
 * `(kind) → the permission that gates retrieval of that kind's export`. Both
 * histories are readable by whoever may read the record: they are the record's
 * own access and sharing trails, and the reader is exactly the population the
 * duty asks to have one answer for.
 *
 * Gate E12.5 pass 1 (RECORD 17) states the consequence plainly: PRODUCING an
 * export requires `issued_record:export`, but RETRIEVING the produced artefact
 * requires only `issued_record:read`, so a holder of `:read` alone can download
 * a history someone else produced. That is deliberate under the 64.92.0 kinded
 * -export contract — the artefact contains nothing its holder could not read
 * from the record — and not an oversight.
 */
export const ISSUED_RECORD_EXPORT_GATES = {
    access_history: Permission.ISSUED_RECORD_READ,
    sharing_history: Permission.ISSUED_RECORD_READ,
};
// ── Collections ──
export const ISSUED_RECORDS_COLLECTION = 'grc_issued_records';
export const ISSUED_RECORD_VERSIONS_COLLECTION = 'grc_issued_record_versions';
export const ISSUED_RECORD_CONSENTS_COLLECTION = 'grc_issued_record_consents';
export const ISSUED_RECORD_SHARING_EVENTS_COLLECTION = 'grc_issued_record_sharing_events';
export const ISSUED_RECORD_SCOPE_DECISIONS_COLLECTION = 'grc_issued_record_scope_decisions';
export const ISSUED_RECORD_EVIDENCE_COLLECTION = 'grc_issued_record_evidence';
// ── Error codes ──
export const ISSUED_RECORDS_ERROR_CODES = {
    RECORD_NOT_FOUND: 'ISSUED_RECORD_NOT_FOUND',
    VERSION_NOT_FOUND: 'ISSUED_RECORD_VERSION_NOT_FOUND',
    /** HTTP 409 — no current scope decision exists for the kind (AC2). */
    SCOPE_REQUIRED: 'ISSUED_RECORD_SCOPE_REQUIRED',
    /** HTTP 400 — no installed pack declares the kind. */
    KIND_NOT_DECLARED: 'ISSUED_RECORD_KIND_NOT_DECLARED',
    /**
     * HTTP 400 — the coverage value the pack flags `requires_scope_review` was
     * recorded without the review the institution needs (AC2). Enforced
     * server-side, so an API caller cannot skip what the form asks for.
     */
    SCOPE_REVIEW_REQUIRED: 'ISSUED_RECORD_SCOPE_REVIEW_REQUIRED',
    /** HTTP 405/409 — any attempt to edit or delete an issued version (AC1). */
    VERSION_IMMUTABLE: 'ISSUED_RECORD_VERSION_IMMUTABLE',
    /** HTTP 409 — the compare-and-set found the prior version already superseded. */
    VERSION_ALREADY_SUPERSEDED: 'ISSUED_RECORD_VERSION_ALREADY_SUPERSEDED',
    /** HTTP 400 — a reissuance or correction without a reason. */
    REASON_REQUIRED: 'ISSUED_RECORD_REASON_REQUIRED',
    /** HTTP 409 — the sharing gate refused; the refusal is recorded on the record (AC3). */
    SHARING_REFUSED: 'ISSUED_RECORD_SHARING_REFUSED',
    CONSENT_NOT_FOUND: 'ISSUED_RECORD_CONSENT_NOT_FOUND',
    /** HTTP 400 — a consent for a minor without the representative and relationship. */
    REPRESENTATIVE_REQUIRED: 'ISSUED_RECORD_REPRESENTATIVE_REQUIRED',
    /** HTTP 409 — an installed pack states the retention rule; the deletion is refused and recorded (AC7). */
    RETENTION_BLOCKS_DELETION: 'ISSUED_RECORD_RETENTION_BLOCKS_DELETION',
    /**
     * HTTP 409 — a record kind is declared and no declaring record names the
     * external-ref key that carries its subject identifier, so the erasure gate
     * cannot tell whether the case reaches an issued record. Fail closed (AC7).
     */
    SUBJECT_LINKAGE_UNRESOLVED: 'ISSUED_RECORD_SUBJECT_LINKAGE_UNRESOLVED',
    /**
     * HTTP 409 — the pack's sharing-restriction statement, which a consent-path
     * refusal cites, could not be read. The sharing is refused rather than
     * recorded with a citation nobody checked.
     */
    SHARING_RESTRICTION_UNREADABLE: 'ISSUED_RECORD_SHARING_RESTRICTION_UNREADABLE',
    /**
     * HTTP 409 — the viewer's restricted-template grants could not be resolved,
     * so the admin visibility lift the vendor-questionnaire read depends on does
     * not apply (gate E12.5 pass 2, finding 3). Reading on would suppress an
     * `upcoming` questionnaire again and state "no installed framework declares
     * one", which is the falsehood the AC4 repair removed. A read that could not
     * be resolved is a failed read, stated as one.
     */
    VENDOR_TEMPLATE_GRANTS_UNRESOLVED: 'ISSUED_RECORD_VENDOR_TEMPLATE_GRANTS_UNRESOLVED',
    /** HTTP 400 — an instant without a zone offset (the 62.14.0 receipt-time contract). */
    INSTANT_WITHOUT_ZONE: 'ISSUED_RECORD_INSTANT_WITHOUT_ZONE',
};
//# sourceMappingURL=issued-records.js.map